// Sabit Rapor — Kampanya Listesi (Raporlar → Sabit Raporlar → Tanımlamalar).
//
// Panorama'da karşılığı yok; kriter seti ve kolonlar KAMPANYA TANIMLAMA ekranına göre kuruldu.
// Tür/koşul birimi/hedef etiketleri campaign/campaignUtils.js ile aynı sözlükten gelir —
// rapor ile tanım ekranı aynı dili konuşsun diye oradan içe aktarılır, kopyalanmaz.
//
// Kampanya da fiyat gibi tarih aralıklıdır: varsayılan kapsam seçilen tarihte yürürlükte
// olanlardır, "Tüm kayıtlar" ile süzgeç kapatılır.
//
// İki ekran (Sabit Rapor deseni):
//   1) CampaignListCriteriaPage — Esas Alınacak Tarih, Kapsam, Tür, Koşul Birimi, Müşteri
//      Hedefi, Durum, serbest arama. Canlı "N kampanya kritere uyuyor" önizlemesi.
//   2) CampaignListListPage — /api/detail-reports/campaign-list'ten kampanya kartları.
//      Grid/Grafik/Pivot ReportGrid'den.
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authHeaders, exportToExcel } from './listPageStyles';
import { usePageMeta } from '../hooks/usePageMeta';
import { ReportGrid } from './reports/ReportGrid';
import { TYPE_OPTIONS, CONDITION_UNITS, TARGET_TYPES } from './campaign/campaignUtils';
import { DateInput } from './reports/DateInput';
import { MultiSelect } from './reports/MultiSelect';

const ENDPOINT = '/api/detail-reports/campaign-list';
const REPORT_CODE = '5080';
const REPORT_TITLE = 'Kampanya Listesi';

// ── Yardımcılar ──
const pick = (o, ...keys) => { for (const k of keys) if (o?.[k] !== undefined && o?.[k] !== null && o?.[k] !== '') return o[k]; return undefined; };
const optId = (o) => pick(o, 'id', 'Id');
const optCode = (o) => pick(o, 'textId', 'TextId', 'erpId', 'ErpId');
const optName = (o) => pick(o, 'name', 'Name', 'description', 'Description', 'title', 'Title');
const optLabel = (o) => { const code = optCode(o); const name = optName(o); if (code && name) return `${code} - ${name}`; return name || code || '—'; };
// Lookup listesini MultiSelect'in beklediği {value,label} şekline çevirir (kod - ad).
const asChoices = (arr) => (arr || []).map((o) => ({ value: String(optId(o)), label: optLabel(o) }));
function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
}
const nfC = new Intl.NumberFormat('tr-TR');
const fmtCount = (v) => nfC.format(Number(v) || 0);
const fmtMoney = (v) => (v === null || v === undefined ? '—' : `${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0)} ₺`);
const fmtNum = (v) => (v === null || v === undefined ? '—' : new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 6 }).format(Number(v) || 0));
const dash = (v) => (v === null || v === undefined || v === '' ? '—' : v);

// Etiket sözlükleri kampanya tanımlama ekranıyla ORTAK (campaignUtils) — kod değerini
// kullanıcıya gösterirken aynı Türkçe karşılık kullanılsın diye haritaya çevrilir.
const labelMap = (opts) => Object.fromEntries(opts.map((o) => [o.value, o.label]));
const TYPE_LABELS = labelMap(TYPE_OPTIONS);
const UNIT_LABELS = labelMap(CONDITION_UNITS);
const TARGET_LABELS = labelMap(TARGET_TYPES);

// Ürün/Müşteri kriterleri için lookup. Bu sayfada daha önce hiç lookup yoktu — kriterler
// yalnız sabit listelerdi.
function useLookups() {
  const [lk, setLk] = useState({ products: [], customers: [] });
  useEffect(() => {
    const headers = authHeaders();
    const load = (url) => fetch(url, { headers }).then((r) => (r.ok ? r.json() : [])).then(normalizeList).catch(() => []);
    load('/api/products/Select').then((products) => setLk((s) => ({ ...s, products })));
    load('/api/customers/Select').then((customers) => setLk((s) => ({ ...s, customers })));
  }, []);
  return lk;
}

/* ════════════════════════════ STYLES ════════════════════════════ */
const c = {
  page: { display: 'flex', flexDirection: 'column', gap: 16, padding: 4 },
  card: { background: 'var(--bg-card, #fff)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 18, boxShadow: 'var(--shadow-card)', padding: 20 },
  cardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' },
  cardHeadLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  cardHeadIcon: { width: 40, height: 40, borderRadius: 12, background: 'var(--bg-input, #eef2ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent, #0073ea)' },
  cardTitle: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
  label: { fontSize: 12.5, fontWeight: 800, color: 'var(--text-secondary, #475569)' },
  input: { padding: '11px 12px', borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #0f172a)', fontWeight: 600, fontSize: 13.5, width: '100%', boxSizing: 'border-box' },
  sectionSep: { display: 'flex', alignItems: 'center', gap: 12, margin: '22px 0 4px', fontSize: 11.5, fontWeight: 800, letterSpacing: '0.08em', color: 'var(--text-secondary, #94a3b8)' },
  sepLine: { flex: 1, height: 1, background: 'var(--border-color, #e5e7eb)' },
  btn: { padding: '11px 20px', borderRadius: 12, border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnGhost: { background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)' },
  btnPrimary: { background: 'var(--accent-strong, #1e3a8a)', color: '#fff' },
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '44px 24px', textAlign: 'center' },
  emptyIcon: { width: 64, height: 64, borderRadius: 18, background: 'var(--bg-input, #eef2f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary, #94a3b8)', marginBottom: 6 },
  countChip: { marginTop: 4, padding: '8px 16px', borderRadius: 999, background: 'var(--bg-input, #eef2ff)', color: 'var(--accent, #0073ea)', fontWeight: 800, fontSize: 13.5 },
};
function Field({ label, children }) { return <label style={c.field}><span style={c.label}>{label}</span>{children}</label>; }
function Select({ value, onChange, options, placeholder = 'Tümü' }) {
  return (
    <select style={c.input} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={String(o.value ?? optId(o))} value={String(o.value ?? optId(o))}>{o.label !== undefined ? o.label : optLabel(o)}</option>
      ))}
    </select>
  );
}

// Placeholder seçeneği (value='') ZATEN "Tarihte yürürlükte olanlar" demek — backend
// 'all' dışındaki her değeri yürürlük kapsamı sayar. Listede ayrıca 'active' tutmak
// açılır menüde aynı metni İKİ KEZ gösteriyordu (tester geri bildirimi).
const SCOPE_OPTIONS = [
  { value: 'all', label: 'Tüm kampanyalar' },
];
const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];
// ISO "yyyy-MM-dd" → "dd.MM.yyyy" (ham değer ISO kalır; grid sıralaması kronolojik olsun diye).
const fmtDate = (iso) => { if (!iso) return '—'; const [y, m, d] = String(iso).split('-'); return d ? `${d}.${m}.${y}` : iso; };
function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/* ═══════════════════════ 1) KRİTER EKRANI ═══════════════════════ */
export function CampaignListCriteriaPage() {
  const lk = useLookups();
  usePageMeta(REPORT_TITLE, 'Sabit Raporlar › Tanımlamalar');
  const [refDate, setRefDate] = useState(today);
  const [scope, setScope] = useState('active');
  const [type, setType] = useState([]);
  const [conditionUnit, setConditionUnit] = useState([]);
  const [customerTargetType, setCustomerTargetType] = useState([]);
  const [isActive, setIsActive] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [productIds, setProductIds] = useState([]);
  const [customerIds, setCustomerIds] = useState([]);
  const [q, setQ] = useState('');
  const [preview, setPreview] = useState(null);


  const query = useCallback(() => {
    const p = new URLSearchParams();
    p.set('refDate', refDate);
    if (scope === 'all') p.set('scope', 'all');
    if (type.length) p.set('type', type.join(','));
    if (conditionUnit.length) p.set('conditionUnit', conditionUnit.join(','));
    if (customerTargetType.length) p.set('customerTargetType', customerTargetType.join(','));
    if (isActive) p.set('isActive', isActive);
    if (startDate) p.set('startDate', startDate);
    if (endDate) p.set('endDate', endDate);
    if (productIds.length) p.set('productIds', productIds.join(','));
    if (customerIds.length) p.set('customerIds', customerIds.join(','));
    if (q.trim()) p.set('q', q.trim());
    return p;
  }, [refDate, scope, type, conditionUnit, customerTargetType, isActive,
      startDate, endDate, productIds, customerIds, q]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      const p = query();
      p.set('previewOnly', 'true');
      fetch(`${ENDPOINT}?${p.toString()}`, { headers: authHeaders() })
        .then((r) => (r.ok ? r.json() : null))
        .then((json) => { if (!cancelled) setPreview(json?.data?.totals ?? json?.totals ?? null); })
        .catch(() => { if (!cancelled) setPreview(null); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const clear = () => { setRefDate(today()); setScope('active'); setType([]); setConditionUnit([]); setCustomerTargetType([]);
    setIsActive(''); setStartDate(''); setEndDate(''); setProductIds([]); setCustomerIds([]); setQ(''); };

  const showReport = () => {
    const p = query();
    p.set('embed', 'true');
    const w = Math.min(1720, window.screen.availWidth - 40);
    const h = Math.min(940, window.screen.availHeight - 60);
    const left = Math.max(0, Math.round((window.screen.availWidth - w) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - h) / 2));
    window.open(`/reports/campaign-list-list?${p.toString()}`, 'kampanya5080',
      `popup=yes,noopener,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
  };

  return (
    <div style={c.page}>
      <div style={c.card}>
        <div style={c.cardHead}>
          <div style={c.cardHeadLeft}>
            <span style={c.cardHeadIcon}>
              <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            </span>
            <span style={c.cardTitle}>Kriterler</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" style={{ ...c.btn, ...c.btnGhost }} onClick={clear}>✕ Temizle</button>
            <button type="button" style={{ ...c.btn, ...c.btnPrimary }} onClick={showReport}>
              <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
              Raporu Göster
            </button>
          </div>
        </div>

        <div style={c.grid}>
          
          <Field label="İskonto Geçerlilik Tarihi">
            <DateInput value={refDate} onChange={(v) => setRefDate(v)} />
          </Field>
          <Field label="Kapsam"><Select value={scope} onChange={setScope} options={SCOPE_OPTIONS} placeholder="Tarihte yürürlükte olanlar" /></Field>
        </div>

        {/* Geçerlilik tarihi "o gün yürürlükte olanlar"ı sorar; aşağıdaki ikisi ise kampanyanın
            KENDİ başlangıç/bitiş tarihini süzer — "şu tarihten sonra başlayan", "şu tarihten
            önce biten". Farklı sorulardır. */}
        {/* Dört alan TEK satırda: ızgara auto-fill olduğu için geniş ekranda yan yana dizilir,
            dar ekranda kendiliğinden alta kayar. İki ayrı bölüm gereksiz yer kaplıyordu. */}
        <div style={c.sectionSep}><span>İSKONTO TARİHLERİ · ÜRÜN / MÜŞTERİ</span><span style={c.sepLine} /></div>
        <div style={c.grid}>
          <Field label="İsk. Başlangıç Tarihi"><DateInput value={startDate} onChange={setStartDate} /></Field>
          <Field label="İsk. Bitiş Tarihi"><DateInput value={endDate} onChange={setEndDate} /></Field>
          <Field label="Ürün"><MultiSelect value={productIds} onChange={setProductIds} options={asChoices(lk.products)} /></Field>
          <Field label="Müşteri"><MultiSelect value={customerIds} onChange={setCustomerIds} options={asChoices(lk.customers)} /></Field>
        </div>

        <div style={c.sectionSep}><span>DİĞER</span><span style={c.sepLine} /></div>
        <div style={c.grid}>
          <Field label="Durum"><Select value={isActive} onChange={setIsActive} options={ACTIVE_OPTIONS} /></Field>
          <Field label="Kampanya Türü"><MultiSelect value={type} onChange={setType} options={TYPE_OPTIONS} /></Field>
          <Field label="Koşul Birimi"><MultiSelect value={conditionUnit} onChange={setConditionUnit} options={CONDITION_UNITS} /></Field>
          <Field label="Müşteri Hedefi"><MultiSelect value={customerTargetType} onChange={setCustomerTargetType} options={TARGET_TYPES} /></Field>
          <Field label="Kod / Ad Ara">
            <input style={c.input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Kampanya kodu, adı veya açıklaması" />
          </Field>
        </div>
      </div>

      <div style={c.card}>
        <div style={c.emptyState}>
          <span style={c.emptyIcon}>
            <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v9H4v-9" /><path d="M22 7H2v5h20V7z" /><path d="M12 22V7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></svg>
          </span>
          <strong style={{ fontSize: 17, color: 'var(--text-primary, #0f172a)' }}>Rapor ayrı ekranda görüntülenir</strong>
          <span style={{ fontSize: 13.5, color: 'var(--text-secondary, #64748b)', maxWidth: 620 }}>
            Varsayılan olarak <strong>seçilen tarihte yürürlükte</strong> olan kampanyalar listelenir;
            geçmiş/gelecek tanımları görmek için Kapsam'ı <strong>Tüm kampanyalar</strong> yapın.
            Ürün/müşteri seçim listeleri rapora taşınmaz — rapor <strong>hedef türünü ve kısıtları</strong> gösterir,
            ayrıntı kampanya detay ekranındadır.
          </span>
          {preview && <div style={c.countChip}>{fmtCount(preview.kampanyaSayisi)} kampanya kritere uyuyor</div>}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════ 2) RAPOR EKRANI ═══════════════════════ */
const codeChipStyle = { padding: '3px 10px', borderRadius: 8, background: 'var(--bg-input, #eef2ff)', color: 'var(--accent, #0073ea)', fontWeight: 800, fontSize: 12.5 };
function YururChip({ v }) {
  const on = v === 'Evet';
  return (
    <span style={{ padding: '3px 10px', borderRadius: 999, fontWeight: 700, fontSize: 12,
      background: on ? 'rgba(22,163,74,0.10)' : 'var(--bg-input, #f1f5f9)',
      color: on ? '#16a34a' : '#94a3b8' }}>{v}</span>
  );
}
function DurumChip({ v }) {
  if (!v || v === '—') return '—';
  const tone = v === 'Aktif' ? { bg: 'rgba(22,163,74,0.10)', fg: '#16a34a' }
    : v === 'Pasif' ? { bg: 'var(--bg-input, #f1f5f9)', fg: '#94a3b8' }
      : { bg: 'rgba(217,119,6,0.10)', fg: '#d97706' };
  return <span style={{ padding: '3px 10px', borderRadius: 999, background: tone.bg, color: tone.fg, fontWeight: 700, fontSize: 12 }}>{v}</span>;
}

const COLUMNS = [
  { key: 'kod', label: 'Kampanya Kodu', sticky: true, cell: (row) => (row.kod ? <span style={codeChipStyle}>{row.kod}</span> : '—') },
  { key: 'ad', label: 'Kampanya Adı', bold: true },
  { key: 'tur', label: 'Tür', fmt: (v) => TYPE_LABELS[v] ?? v ?? '—' },
  { key: 'kosulBirimi', label: 'Koşul Birimi', fmt: (v) => UNIT_LABELS[v] ?? v ?? '—' },
  { key: 'musteriHedefi', label: 'Müşteri Hedefi', fmt: (v) => TARGET_LABELS[v] ?? v ?? '—' },
  { key: 'esikAdet', label: 'Eşik Adet', num: true, agg: 'none', fmt: fmtNum },
  { key: 'bedelsizAdet', label: 'Bedelsiz Adet', num: true, agg: 'none', fmt: fmtNum },
  { key: 'esikTutar', label: 'Eşik Tutarı', num: true, agg: 'none', fmt: fmtMoney },
  { key: 'indirimYuzde', label: 'İndirim %', num: true, agg: 'none', fmt: (v) => (v === null || v === undefined ? '—' : `%${fmtNum(v)}`) },
  { key: 'indirimTutar', label: 'İndirim ₺', num: true, agg: 'none', fmt: fmtMoney },
  { key: 'butce', label: 'Bütçe', num: true, fmt: fmtMoney },
  { key: 'maksUygulama', label: 'Maks. Uygulama', num: true, agg: 'none', fmt: fmtNum },
  { key: 'maksMusteri', label: 'Maks. / Müşteri', num: true, agg: 'none', fmt: fmtNum },
  { key: 'maksDistributor', label: 'Maks. / Distribütör', num: true, agg: 'none', fmt: fmtNum },
  { key: 'maksGun', label: 'Maks. / Gün', num: true, agg: 'none', fmt: fmtNum },
  { key: 'oncelik', label: 'Öncelik', num: true, agg: 'none', fmt: fmtNum },
  { key: 'birlesebilir', label: 'Birleşebilir', fmt: dash },
  { key: 'onKosullu', label: 'Ön Koşullu', fmt: dash },
  { key: 'baslangic', label: 'Başlangıç', fmt: fmtDate },
  { key: 'bitis', label: 'Bitiş', fmt: (v) => (v ? fmtDate(v) : 'Süresiz') },
  { key: 'yururlukte', label: 'Yürürlükte', cell: (row) => <YururChip v={row.yururlukte} /> },
  { key: 'durum', label: 'Durum', cell: (row) => <DurumChip v={row.durum} /> },
  { key: 'aciklama', label: 'Açıklama', fmt: dash },
];

const l = {
  wrap: { display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-page, #f1f5f9)' },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', padding: '14px 20px', background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #e5e7eb)', position: 'sticky', top: 0, zIndex: 5 },
  topLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  titleIcon: { width: 42, height: 42, borderRadius: 12, background: 'var(--accent-strong, #1e3a8a)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  title: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary, #0f172a)' },
  subtitle: { fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary, #64748b)' },
  iconBtn: { width: 42, height: 42, borderRadius: 12, border: '1px solid var(--border-color, #e5e7eb)', background: 'var(--bg-card, #fff)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  critBar: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', flexWrap: 'wrap', background: 'var(--bg-card, #fff)', borderBottom: '1px solid var(--border-color, #eef2f7)', fontSize: 12.5 },
  critChip: { padding: '6px 12px', borderRadius: 999, background: 'var(--bg-input, #f1f5f9)', color: 'var(--text-secondary, #475569)', fontWeight: 700 },
  empty: { padding: 60, textAlign: 'center', color: 'var(--text-secondary, #94a3b8)', fontWeight: 700 },
};
const hesc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
function printAsPdf({ rows, subtitle }) {
  const head = COLUMNS.map((col) => `<th style="text-align:${col.num ? 'right' : 'left'}">${hesc(col.label)}</th>`).join('');
  const body = rows.map((row) => `<tr>${COLUMNS.map((col) => {
    let v = row[col.key];
    if (col.fmt) v = col.fmt(v);
    return `<td style="text-align:${col.num ? 'right' : 'left'}">${hesc(v ?? '')}</td>`;
  }).join('')}</tr>`).join('');
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${REPORT_TITLE}</title><style>*{box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;color:#0f172a;margin:0;padding:14px}h1{font-size:14px;color:#1e3a8a;margin:0 0 4px}.crit{font-size:9.5px;color:#475569;margin-bottom:8px}table{width:100%;border-collapse:collapse}th{background:#eef2f7;color:#475569;font-size:8px;text-transform:uppercase;padding:4px 5px;border-bottom:1.5px solid #cbd5e1}td{font-size:9px;padding:3px 5px;border-bottom:1px solid #eef2f7;white-space:nowrap}@page{size:A3 landscape;margin:10mm}</style></head><body><h1>${REPORT_TITLE}</h1><div class="crit">${hesc(subtitle)}</div><table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table></body></html>`;
  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document; doc.open(); doc.write(html); doc.close();
  iframe.onload = () => { iframe.contentWindow.focus(); iframe.contentWindow.print(); setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 1000); };
}

export function CampaignListListPage() {
  usePageMeta(REPORT_TITLE, 'Sabit Rapor');
  const [sp] = useSearchParams();

  const params = useMemo(() => {
    const o = {};
    for (const k of ['refDate', 'scope', 'type', 'conditionUnit', 'customerTargetType', 'isActive',
      'startDate', 'endDate', 'productIds', 'customerIds', 'q']) { const v = sp.get(k); if (v) o[k] = v; }
    return o;
  }, [sp]);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleRows, setVisibleRows] = useState(null);
  const [nowLabel] = useState(() => new Date().toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }));

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams(params).toString();
      const res = await fetch(`${ENDPOINT}?${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Rapor alınamadı (${res.status})`);
      const json = await res.json();
      const data = json?.data ?? json;
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotals(data?.totals ?? null);
    } catch (e) {
      setError(e.message || 'Hata'); setRows([]);
    } finally { setLoading(false); }
  }, [params]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const exportRows = visibleRows ?? rows;
  const scopeLabel = params.scope === 'all' ? 'Tüm kampanyalar' : 'Yürürlükte olanlar';
  const typeLabel = params.type ? (TYPE_LABELS[params.type] ?? params.type) : 'Tümü';
  const unitLabel = params.conditionUnit ? (UNIT_LABELS[params.conditionUnit] ?? params.conditionUnit) : 'Tümü';
  const targetLabel = params.customerTargetType ? (TARGET_LABELS[params.customerTargetType] ?? params.customerTargetType) : 'Tümü';
  const statusLabel = params.isActive === 'true' ? 'Aktif' : params.isActive === 'false' ? 'Pasif' : 'Tümü';
  // Yeni kriterlerin rozeti/özeti: verilmediyse hiç yazılmaz (özet uzamasın).
  const startRange = params.startDate ? fmtDate(params.startDate) : '';
  const endRange = params.endDate ? fmtDate(params.endDate) : '';
  const csvCount = (v) => (v ? String(v).split(',').filter(Boolean).length : 0);
  const productCount = csvCount(params.productIds);
  const customerCount = csvCount(params.customerIds);
  const summaryText = [
    `Tarih: ${fmtDate(params.refDate)}`, `Kapsam: ${scopeLabel}`, `Tür: ${typeLabel}`,
    `Koşul: ${unitLabel}`, `Hedef: ${targetLabel}`, `Durum: ${statusLabel}`,
    startRange && `İsk. Başlangıç: ${startRange}`,
    endRange && `İsk. Bitiş: ${endRange}`,
    productCount && `Ürün: ${productCount} seçili`,
    customerCount && `Müşteri: ${customerCount} seçili`,
    params.q && `Arama: ${params.q}`,
  ].filter(Boolean).join(' · ');

  const onExcel = () => {
    const cols = COLUMNS.map((col) => ({ field: col.key, headerName: col.label, valueGetter: ({ row }) => row[col.key] }));
    exportToExcel(exportRows, cols, `${REPORT_CODE}_kampanya_listesi`);
  };

  return (
    <div style={l.wrap}>
      <div style={l.topbar}>
        <div style={l.topLeft}>
          <span style={l.titleIcon}>
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12v9H4v-9" /><path d="M22 7H2v5h20V7z" /><path d="M12 22V7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" /><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></svg>
          </span>
          <div>
            <div style={l.title}>{REPORT_TITLE}</div>
            <div style={l.subtitle}>
              {loading ? 'Yükleniyor…' : `${fmtCount(totals?.kampanyaSayisi ?? rows.length)} kampanya · ${fmtCount(totals?.aktifSayisi ?? 0)} aktif · ${fmtCount(totals?.yururlukteSayisi ?? 0)} yürürlükte · ${nowLabel}`}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" style={l.iconBtn} title="PDF İndir" onClick={() => printAsPdf({ rows: exportRows, subtitle: summaryText })}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Excel'e Aktar" onClick={onExcel}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#16794c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="m9 15 3 3 3-3" /><path d="M12 12v6" /></svg>
          </button>
          <button type="button" style={l.iconBtn} title="Yazdır" onClick={() => window.print()}>
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
          </button>
        </div>
      </div>

      <div style={l.critBar}>
        <span style={{ fontWeight: 800, color: 'var(--text-secondary, #64748b)' }}>KRİTERLER</span>
        <span style={l.critChip}>Tarih: {fmtDate(params.refDate)}</span>
        <span style={l.critChip}>Kapsam: {scopeLabel}</span>
        {startRange && <span style={l.critChip}>İsk. Başlangıç: {startRange}</span>}
        {endRange && <span style={l.critChip}>İsk. Bitiş: {endRange}</span>}
        {productCount > 0 && <span style={l.critChip}>Ürün: {productCount} seçili</span>}
        {customerCount > 0 && <span style={l.critChip}>Müşteri: {customerCount} seçili</span>}
        <span style={l.critChip}>Tür: {typeLabel}</span>
        <span style={l.critChip}>Koşul: {unitLabel}</span>
        <span style={l.critChip}>Hedef: {targetLabel}</span>
        <span style={l.critChip}>Durum: {statusLabel}</span>
        {params.q && <span style={l.critChip}>Arama: {params.q}</span>}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '10px 20px 20px' }}>
        {error && <div style={{ ...l.empty, color: '#dc2626' }}>{error}</div>}
        {!error && !loading && (
          <ReportGrid
            reportKey="campaign-list"
            columns={COLUMNS}
            rows={rows}
            onVisibleRowsChange={setVisibleRows}
          />
        )}
        {loading && <div style={l.empty}>Yükleniyor…</div>}
      </div>
    </div>
  );
}
